import React, { useEffect, useMemo, useState } from 'react';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Search, Clock, Flame, Loader2, Check, ChefHat, Sparkles, ArrowRight, Wand2 } from 'lucide-react';
import { toast } from 'sonner';
import { listRecipes, getRecipe, logRecipe, getRecipeTags } from '../lib/api';
import Seo from '../components/Seo';
import IngredientSuggestModal from '../components/IngredientSuggestModal';

const FALLBACK_IMG = "data:image/svg+xml;utf8," + encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 300"><defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#047857"/><stop offset="1" stop-color="#065f46"/></linearGradient></defs><rect width="400" height="300" fill="url(%23g)"/><g fill="none" stroke="#ffffff" stroke-width="6" stroke-linecap="round" stroke-linejoin="round"><rect x="140" y="130" width="120" height="90" rx="12"/><rect x="170" y="115" width="60" height="18" rx="4"/><circle cx="200" cy="175" r="26"/></g></svg>'
);

const RecipeImg = ({ src, alt, className }) => {
  const [broken, setBroken] = React.useState(false);
  return (
    <img
      src={broken ? FALLBACK_IMG : src}
      alt={alt}
      loading="lazy"
      referrerPolicy="no-referrer"
      onError={() => setBroken(true)}
      className={className}
    />
  );
};

const TAG_LABEL = {
  'all': 'All',
  'high-protein': 'High protein',
  'low-carb': 'Low carb',
  'quick': 'Quick',
  'vegetarian': 'Vegetarian',
  'keto': 'Keto',
  'breakfast': 'Breakfast',
  'lunch': 'Lunch',
  'dinner': 'Dinner',
  'snack': 'Snack',
  'gluten-free': 'Gluten-free',
};

const Recipes = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [tags, setTags] = useState([]);
  const [tag, setTag] = useState('all');
  const [q, setQ] = useState('');
  const [fits, setFits] = useState(false);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(true);
  const [locked, setLocked] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [servings, setServings] = useState(1);
  const [logging, setLogging] = useState(false);
  const [suggestOpen, setSuggestOpen] = useState(false);

  useEffect(() => { if (!loading && !user) navigate('/'); }, [user, loading, navigate]);
  useEffect(() => { getRecipeTags().then(d => setTags(['all', ...d.tags])).catch(() => {}); }, []);

  useEffect(() => {
    if (!user) return;
    setBusy(true);
    const params = {};
    if (q.trim()) params.q = q.trim();
    if (tag !== 'all') params.tag = tag;
    if (fits) params.fits = true;
    listRecipes(params)
      .then((r) => { setItems(r); setLocked(false); })
      .catch((e) => { if (e.response?.status === 402) setLocked(true); else toast.error('Failed to load recipes'); })
      .finally(() => setBusy(false));
  }, [user, q, tag, fits]);

  const openRecipe = async (id) => {
    setDetailLoading(true);
    try { setDetail(await getRecipe(id)); setServings(1); }
    catch (e) { toast.error('Recipe unavailable'); }
    finally { setDetailLoading(false); }
  };

  const logIt = async () => {
    if (!detail) return;
    setLogging(true);
    try {
      await logRecipe(detail.id, servings);
      toast.success(`${detail.title} logged to today\u2019s diary`);
      setDetail(null);
    } catch (e) { toast.error('Could not log recipe'); }
    finally { setLogging(false); }
  };

  const proTarget = user?.daily_target_calories;

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#fefdf9]">
      <Seo title="Recipe library — CalorieSnap Pro" description="Search high-protein, low-carb and other recipes tuned to your daily calorie target. Log any recipe to your diary in one tap." canonical="https://www.thecaloriesnap.com/app/recipes" />
      <Navbar variant="app" />
      <main className="max-w-7xl mx-auto px-5 lg:px-8 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <p className="text-emerald-700 font-semibold text-sm uppercase tracking-widest mb-1">Recipe library</p>
            <h1 className="font-display text-4xl md:text-5xl font-bold text-emerald-950">Real food, right numbers</h1>
            <p className="text-emerald-950/70 mt-1">Curated recipes with real nutrition data. Or snap what's in your fridge and let AI suggest ideas.</p>
          </div>
          <div className="flex items-center gap-3">
            <Button onClick={() => setSuggestOpen(true)} className="bg-orange-500 hover:bg-orange-600 text-white rounded-full h-11 px-5 font-semibold" disabled={locked}><Wand2 className="w-4 h-4 mr-2" />Suggest from ingredients</Button>
            {proTarget && (
              <div className="text-right">
                <p className="text-xs text-emerald-950/50 uppercase tracking-widest">Daily target</p>
                <p className="font-display text-2xl font-bold text-emerald-950">{proTarget} kcal</p>
              </div>
            )}
          </div>
        </div>

        {locked ? (
          <Card className="p-10 rounded-3xl border-emerald-950/5 bg-gradient-to-br from-emerald-700 to-emerald-800 text-white text-center">
            <ChefHat className="w-10 h-10 mx-auto mb-3 opacity-80" />
            <h2 className="font-display text-2xl font-bold mb-2">Recipes are a Pro feature</h2>
            <p className="text-white/85 max-w-md mx-auto mb-5">Unlock 24+ recipes with real macros, tuned to your daily target. Includes high-protein, low-carb, quick and vegetarian options.</p>
            <Button className="bg-white text-emerald-800 hover:bg-emerald-50 rounded-full h-11 px-6 font-semibold" onClick={() => navigate('/pricing')}>Upgrade to Pro <ArrowRight className="w-4 h-4 ml-2" /></Button>
          </Card>
        ) : (
          <>
            {/* Filters */}
            <Card className="p-4 rounded-2xl border-emerald-950/5 bg-white mb-5 flex flex-wrap items-center gap-3">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-emerald-950/40" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search recipes or ingredients…" className="pl-9 h-10 border-emerald-950/10" />
              </div>
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input type="checkbox" checked={fits} onChange={(e) => setFits(e.target.checked)} className="w-4 h-4 accent-emerald-700" />
                <span className="text-sm text-emerald-950">Fits my remaining calories today</span>
              </label>
            </Card>

            {/* Tag chips */}
            <div className="flex flex-wrap gap-2 mb-6">
              {tags.map(t => (
                <button key={t} onClick={() => setTag(t)} className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${tag === t ? 'bg-emerald-700 text-white' : 'bg-white border border-emerald-950/10 text-emerald-950/70 hover:border-emerald-700'}`}>
                  {TAG_LABEL[t] || t}
                </button>
              ))}
            </div>

            {/* Grid */}
            {busy ? (
              <Card className="p-16 text-center rounded-3xl border-emerald-950/5 bg-white"><Loader2 className="w-8 h-8 animate-spin text-emerald-500 mx-auto" /></Card>
            ) : items.length === 0 ? (
              <Card className="p-16 text-center rounded-3xl border-emerald-950/5 bg-white">
                <ChefHat className="w-10 h-10 text-emerald-300 mx-auto mb-3" />
                <p className="text-emerald-950/70">No recipes match. Try a different filter or clear the "fits my target" toggle.</p>
              </Card>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {items.map(r => (
                  <button key={r.id} onClick={() => openRecipe(r.id)} className="group text-left bg-white rounded-3xl border border-emerald-950/5 overflow-hidden hover:shadow-lg transition-all hover:-translate-y-1">
                    <div className="aspect-[4/3] overflow-hidden bg-emerald-50">
                      <RecipeImg src={r.image} alt={r.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                    <div className="p-4">
                      <div className="flex flex-wrap gap-1 mb-2">
                        {(r.tags || []).slice(0, 2).map(t => <Badge key={t} variant="secondary" className="bg-emerald-50 text-emerald-800 hover:bg-emerald-50 text-[10px] uppercase tracking-wide">{TAG_LABEL[t] || t}</Badge>)}
                      </div>
                      <h3 className="font-display font-bold text-emerald-950 leading-snug mb-2">{r.title}</h3>
                      <div className="flex items-center gap-3 text-xs text-emerald-950/60">
                        <span className="inline-flex items-center gap-1"><Flame className="w-3.5 h-3.5" />{r.calories} kcal</span>
                        <span className="inline-flex items-center gap-1"><span className="font-semibold text-emerald-700">{r.protein}g</span> protein</span>
                        <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{r.prep_min + r.cook_min}m</span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </main>

      {/* Detail modal */}
      <Dialog open={!!detail} onOpenChange={(v) => { if (!v) setDetail(null); }}>
        <DialogContent className="sm:max-w-2xl rounded-2xl p-0 overflow-hidden max-h-[90vh] overflow-y-auto">
          {detailLoading ? (
            <div className="p-16 text-center"><Loader2 className="w-8 h-8 text-emerald-500 mx-auto animate-spin" /></div>
          ) : detail ? (
            <div>
              <div className="relative h-56 bg-emerald-50">
                <RecipeImg src={detail.image} alt={detail.title} className="w-full h-full object-cover" />
              </div>
              <div className="p-6">
                <DialogHeader className="text-left mb-4">
                  <div className="flex flex-wrap gap-1 mb-2">
                    {(detail.tags || []).map(t => <Badge key={t} variant="secondary" className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 text-[10px] uppercase tracking-wide">{TAG_LABEL[t] || t}</Badge>)}
                  </div>
                  <DialogTitle className="font-display text-2xl text-emerald-950">{detail.title}</DialogTitle>
                  <DialogDescription>{detail.servings || 1} serving{(detail.servings || 1) > 1 ? 's' : ''} · {detail.prep_min}m prep · {detail.cook_min}m cook</DialogDescription>
                </DialogHeader>
                <div className="grid grid-cols-4 gap-2 mb-6">
                  {[['Calories', detail.calories, 'kcal'], ['Protein', detail.protein, 'g'], ['Carbs', detail.carbs, 'g'], ['Fat', detail.fat, 'g']].map(([l, v, u]) => (
                    <div key={l} className="bg-emerald-50 rounded-xl py-3 text-center">
                      <div className="font-display text-lg font-bold text-emerald-900">{Math.round(v * servings)}{u}</div>
                      <div className="text-[10px] uppercase tracking-wider text-emerald-800/60">{l}</div>
                    </div>
                  ))}
                </div>
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <h4 className="font-display font-bold text-emerald-950 mb-2">Ingredients</h4>
                    <ul className="space-y-1.5 text-sm text-emerald-950/80">
                      {detail.ingredients.map((i, idx) => <li key={idx} className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />{i}</li>)}
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-display font-bold text-emerald-950 mb-2">Method</h4>
                    <ol className="space-y-2 text-sm text-emerald-950/80">
                      {detail.steps.map((s, idx) => <li key={idx} className="flex items-start gap-2"><span className="w-5 h-5 rounded-full bg-emerald-700 text-white text-xs font-bold grid place-items-center shrink-0">{idx + 1}</span>{s}</li>)}
                    </ol>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-3 pt-4 border-t border-emerald-950/5">
                  <label className="text-sm text-emerald-950/70">Servings</label>
                  <Input type="number" min="0.25" max="4" step="0.25" value={servings} onChange={(e) => setServings(Math.max(0.25, Math.min(4, parseFloat(e.target.value) || 1)))} className="w-20 h-10" />
                  <Button onClick={logIt} disabled={logging} className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-full h-11 ml-auto">{logging ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}Log to diary</Button>
                </div>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
      <IngredientSuggestModal open={suggestOpen} onClose={() => setSuggestOpen(false)} />
    </div>
  );
};

export default Recipes;
